// Shared number/time formatting for price and returns displays. Kept framework-free so
// any page (dashboard, watchlist) renders figures identically.

export function money(value: number | undefined | null, currency = "USD"): string {
  if (value === undefined || value === null) return "—";
  return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(value);
}

/** Signed percent with a sign prefix, e.g. "+12.3%", "-4.1%". */
export function signedPercent(value: number | undefined | null): string {
  if (value === undefined || value === null) return "—";
  const sign = value > 0 ? "+" : "";
  return `${sign}${value.toFixed(2)}%`;
}

/** Annualized compound growth from a cumulative percent return over a fixed span. */
export function cagr(
  totalReturn: number | undefined | null,
  years: number,
): number | null {
  if (totalReturn === undefined || totalReturn === null || years <= 0 || totalReturn < -100) {
    return null;
  }
  return (Math.pow(1 + totalReturn / 100, 1 / years) - 1) * 100;
}

/** Tailwind text color for a signed figure: green up, red down, muted when absent. */
export function changeColor(value: number | undefined | null): string {
  if (value === undefined || value === null) return "text-muted-foreground";
  if (value > 0) return "text-success";
  if (value < 0) return "text-destructive";
  return "";
}

/** A "since Mon YYYY (N.N yrs)" label for a window's start date, for Max/inception spans.
    Returns an empty string when the date is missing or unparseable. */
export function sinceSpan(iso: string | null | undefined): string {
  if (!iso) return "";
  const start = new Date(iso);
  if (Number.isNaN(start.getTime())) return "";
  const month = start.toLocaleString(undefined, { month: "short", year: "numeric" });
  const years = (Date.now() - start.getTime()) / (365.25 * 24 * 3600 * 1000);
  if (years < 1) return `since ${month}`;
  return `since ${month} (${years.toFixed(1)} yrs)`;
}

// Trailing filler words common to fund names that add little on a cramped row.
// Stripped from the end only, so "Total International Stock" survives but a trailing
// "Index Fund" or "ETF" is dropped.
const NAME_FILLER = new Set([
  "fund",
  "etf",
  "index",
  "trust",
  "shares",
  "the",
]);

/** A compact security name for cramped rows. Drops trailing filler words like
    "Index Fund" or "ETF" and strips a "Series N" tail. Pass null to let the
    containing layout handle truncation instead of applying a character cap. The
    full name should stay available as a tooltip. Returns "" for empty input. */
export function shortName(
  name: string | undefined | null,
  maxLength: number | null = 28,
): string {
  if (!name) return "";
  // Drop a trailing "Series 1" / "Series II" style tail, vendor bookkeeping noise.
  let cleaned = name.trim().replace(/\s+series\s+[0-9ivx]+$/i, "");
  const words = cleaned.split(/\s+/);
  while (words.length > 1 && NAME_FILLER.has(words[words.length - 1].toLowerCase())) {
    words.pop();
  }
  cleaned = words.join(" ");
  if (maxLength != null && cleaned.length > maxLength) {
    cleaned = cleaned.slice(0, maxLength).replace(/\s+\S*$/, "").trimEnd() + "…";
  }
  return cleaned;
}

/** Compact relative time like "3m ago", for the "prices as of" line. */
export function timeAgo(iso: string | null | undefined): string {
  if (!iso) return "never";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "never";
  const seconds = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}
